import React, { useState, useEffect } from 'react';
import { X, ArrowRight, Sparkles, ExternalLink } from 'lucide-react';
import { getMediaForSlot } from '../../lib/mediaResolver';
import { StargazeImage } from '../common/StargazeImage';
import { StargazeHorizontalLogo } from '../common/StargazeHorizontalLogo';

interface FullscreenNavOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavPreviewItem {
  image: string;
  focalPoint?: { x: number; y: number };
  tag: string;
  title: string;
  desc: string;
}

const DEFAULT_NAV_PREVIEWS: Record<string, NavPreviewItem> = {
  HOME: {
    image: 'https://images.unsplash.com/photo-1517604931442-7e0c8ed2963c?auto=format&fit=crop&w=1200&q=80',
    tag: 'STUDIO & FOUNDERS',
    title: 'THE STARGAZE UNIVERSE',
    desc: 'Cinema studio leadership led by Satish Mohod, Narayan Mohod, and Nikhil Shirbhate.',
  },
  FILMS: {
    image: 'https://images.unsplash.com/photo-1485846234645-a62644f84728?auto=format&fit=crop&w=1200&q=80',
    tag: 'CINEMA SLATE',
    title: 'SAHEB VIKAS KARI & FILM SLATE',
    desc: 'Original features, short drama, and series produced with uncompromising artistic ambition.',
  },
  EVENTS: {
    image: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=1200&q=80',
    tag: 'ENTERTAINMENT & EVENTS',
    title: 'FATHER & SON DUO LIVE',
    desc: 'Immersive live music concert & theatrical tour featuring Narayan Mohod & Satish Mohod.',
  },
  RENTAL: {
    image: 'https://images.unsplash.com/photo-1512790182412-b19e6d62bc39?auto=format&fit=crop&w=1200&q=80',
    tag: 'CAMERA DEPARTMENT',
    title: 'ARRI ALEXA 35 & COOKE ANAMORPHICS',
    desc: 'State-of-the-art camera systems & optical rental infrastructure for cinematographers.',
  },
  'PR & MARKETING': {
    image: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=1200&q=80',
    tag: 'COMMUNICATIONS DIVISION',
    title: 'THEATRICAL PR & PUBLICITY',
    desc: 'High-impact civic campaigns (Nagpur Police 112) and nationwide entertainment PR syndication.',
  },
  WORK: {
    image: 'https://images.unsplash.com/photo-1536440136628-849c177e76a1?auto=format&fit=crop&w=1200&q=80',
    tag: 'ALL CREATIVE WORK',
    title: 'CURATED PORTFOLIO',
    desc: 'Comprehensive showcase of films, music concerts, campaigns, and creative productions.',
  },
  CONTACT: {
    image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=80',
    tag: 'CONNECT WITH STUDIO',
    title: 'PRODUCTION DESK & OFFICES',
    desc: 'Consult with our producers on co-productions, rentals, distribution, and project booking.',
  },
  EXPERIENCES: {
    image: 'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?auto=format&fit=crop&w=1200&q=80',
    tag: 'FAN ACTIVATIONS',
    title: 'FAN EXPERIENCES & SCREENINGS',
    desc: 'Director masterclasses, VIP festival screenings, red carpet galas, and bespoke audio-visual activations.',
  },
  DISTRIBUTION: {
    image: 'https://images.unsplash.com/photo-1509198397868-475647b2a1e5?auto=format&fit=crop&w=1200&q=80',
    tag: 'WORLDWIDE RIGHTS',
    title: 'GLOBAL LICENSING & SYNDICATION',
    desc: 'Connecting groundbreaking regional and national cinema with international buyers, OTT platforms, and theatrical circuits.',
  },
  NEWSROOM: {
    image: 'https://images.unsplash.com/photo-1485846234645-a62644f84728?auto=format&fit=crop&w=1200&q=80',
    tag: 'PRESS & INSIGHTS',
    title: 'STUDIO ANNOUNCEMENT',
    desc: 'Latest company press releases, awards, and cinema technology reports.',
  },
};

export const FullscreenNavOverlay: React.FC<FullscreenNavOverlayProps> = ({ isOpen, onClose }) => {
  const [activeNav, setActiveNav] = useState<string>('FILMS');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [navPreviews, setNavPreviews] = useState<Record<string, NavPreviewItem>>(DEFAULT_NAV_PREVIEWS);

  useEffect(() => {
    let isMounted = true;
    async function loadDynamicMedia() {
      const [logoRes, workRes, distRes, expRes] = await Promise.all([
        getMediaForSlot('brand.logo'),
        getMediaForSlot('hero.sahebVikaskari.desktop'),
        getMediaForSlot('hero.psycho.desktop'),
        getMediaForSlot('events.fatherSon'),
      ]);

      if (!isMounted) return;

      if (logoRes.url) {
        setLogoUrl(logoRes.url);
      }

      setNavPreviews((prev) => ({
        ...prev,
        FILMS: {
          ...prev.FILMS,
          image: workRes.url || prev.FILMS.image,
          focalPoint: workRes.focalPoint,
        },
        EVENTS: {
          ...prev.EVENTS,
          image: expRes.url || prev.EVENTS.image,
          focalPoint: expRes.focalPoint,
        },
        WORK: {
          ...prev.WORK,
          image: distRes.url || prev.WORK.image,
          focalPoint: distRes.focalPoint,
        },
      }));
    }

    if (isOpen) {
      loadDynamicMedia();
    }

    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const currentPreview = navPreviews[activeNav] || navPreviews.WORK;

  return (
    <div className="fixed inset-0 z-[100] bg-white/95 backdrop-blur-2xl text-zinc-900 flex flex-col justify-between overflow-hidden animate-fade-in">
      {/* Moving Ambient Light Backdrop */}
      <div className="absolute inset-0 pointer-events-none opacity-40">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-400/15 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-sky-400/10 rounded-full blur-[150px]" />
      </div>

      {/* Header bar inside overlay */}
      <div className="relative z-10 max-w-7xl w-full mx-auto px-6 py-6 flex items-center justify-between border-b border-zinc-200">
        <div className="flex items-center">
          <StargazeHorizontalLogo
            customLogoUrl={logoUrl || undefined}
            alt="Stargaze Universe"
            className="h-10 w-auto max-w-[200px]"
          />
        </div>

        <button
          onClick={onClose}
          data-cursor="VIEW"
          className="flex items-center gap-2 px-4 py-2 rounded-full border border-zinc-300 hover:border-[#D97706] bg-zinc-100 hover:bg-zinc-200 text-xs font-mono uppercase tracking-widest text-zinc-800 transition"
        >
          <span>CLOSE SCENE</span>
          <X className="w-4 h-4 text-[#D97706]" />
        </button>
      </div>

      {/* Main Overlay Body: 2-Column Grid */}
      <div className="relative z-10 max-w-7xl w-full mx-auto px-6 py-8 flex-1 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        {/* Navigation Column (Left 7 Cols) */}
        <div className="lg:col-span-7 space-y-4">
          <span className="text-xs font-mono text-[#B45309] font-bold tracking-widest uppercase block mb-2">
            PRIMARY DIRECTORY
          </span>

          <nav className="space-y-1">
            {[
              { label: 'HOME', path: '/', sub: 'Home • Cinema Universe • Team & Founders' },
              { label: 'FILMS', path: '/films', sub: 'Feature Slate • Saheb Vikas Kari • Psycho • Series' },
              { label: 'EVENTS', path: '/events', sub: 'Father & Son Duo Live • Premieres • Concerts' },
              { label: 'RENTAL', path: '/rental', sub: 'ARRI Alexa 35 • Sony Venice • Cooke Glass • Gear' },
              { label: 'PR & MARKETING', path: '/marketing', sub: 'Theatrical PR • Awards • Police 112 • Campaigns' },
              { label: 'WORK', path: '/work', sub: 'Complete Curated Portfolio across All Verticals' },
              { label: 'CONTACT', path: '/contact', sub: 'Production Consultation • Office Desk • Enquiries' },
              { label: 'NEWSROOM', path: '/newsroom', sub: 'Official Press Releases • Awards • Industry Reports' },
            ].map((item) => (
              <a
                key={item.label}
                href={item.path}
                onMouseEnter={() => setActiveNav(item.label)}
                data-cursor="VIEW"
                className="group block py-2.5 px-3 rounded-xl hover:bg-zinc-100 border border-transparent hover:border-[#D97706]/40 transition duration-300"
              >
                <div className="flex items-baseline justify-between">
                  <span className="text-2xl sm:text-4xl font-black font-serif-cinematic tracking-tight text-zinc-800 group-hover:text-[#D97706] group-hover:translate-x-2 transition duration-300">
                    {item.label}
                  </span>
                  <ArrowRight className="w-5 h-5 text-[#D97706] opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition duration-300" />
                </div>
                <p className="text-[11px] text-zinc-500 font-mono tracking-wider mt-0.5 opacity-90 group-hover:opacity-100">
                  {item.sub}
                </p>
              </a>
            ))}
          </nav>
        </div>

        {/* Dynamic Project Preview Frame (Right 5 Cols) */}
        <div className="hidden lg:block lg:col-span-5 relative">
          <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-zinc-200 bg-zinc-100 shadow-xl group">
            <StargazeImage
              src={currentPreview.image}
              alt={currentPreview.title}
              focalPoint={currentPreview.focalPoint}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />

            <div className="absolute bottom-0 inset-x-0 p-8 space-y-3">
              <span className="inline-block px-3 py-1 rounded bg-[#D97706] text-white font-mono text-[10px] font-bold tracking-widest uppercase shadow-sm">
                {currentPreview.tag}
              </span>
              <h3 className="text-2xl font-bold font-serif-cinematic text-white leading-tight">
                {currentPreview.title}
              </h3>
              <p className="text-xs text-zinc-200 font-sans leading-relaxed">
                {currentPreview.desc}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Secondary Links inside Overlay */}
      <div className="relative z-10 max-w-7xl w-full mx-auto px-6 py-5 border-t border-zinc-200 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs font-mono">
        <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-zinc-600 font-semibold text-[11px]">
          <a href="/#team" className="hover:text-[#D97706] transition">EXECUTIVE TEAM</a>
          <a href="/capabilities" className="hover:text-[#D97706] transition">CAPABILITIES</a>
          <a href="/distribution" className="hover:text-[#D97706] transition">DISTRIBUTION</a>
          <a href="/about" className="hover:text-[#D97706] transition">ABOUT STUDIO</a>
          <a href="/admin/login" className="hover:text-[#D97706] transition flex items-center gap-1">
            CMS PORTAL <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        <a
          href="/contact"
          className="px-5 py-2.5 rounded-xl bg-[#D97706] hover:bg-[#B45309] text-white font-bold uppercase tracking-wider transition shadow-md shadow-amber-600/20 flex items-center gap-2"
        >
          START A PROJECT <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
};
